import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Dialog } from 'voyager';

import type { TranslationKey } from '@lang';
import { isMissingCopy, useTranslations } from '@lang';
import { modalErrorHide } from '@store/modalError/actions';
import { modalErrorSelectors } from '@store/modalError/selectors';

import { DialogSlide } from './Notifications.styles';
import { resetAfterError } from './resetAfterError';

const DIALOG_TOAST_ID = 'notification-dialog';

const GENERIC_CODES = ['global', 'undefined', 'ERR_BAD_REQUEST'];

type NotificationDialogError = { code?: string } & Record<string, unknown>;

export const NotificationDialog = () => {
  const { t } = useTranslations();
  const dispatch = useDispatch();
  const error = useSelector(modalErrorSelectors.getModalError) as NotificationDialogError | null;
  const preventPageReload = useSelector(
    modalErrorSelectors.getModalErrorPreventPageReload
  ) as boolean;

  useEffect(() => {
    if (!error) {
      toast.dismiss(DIALOG_TOAST_ID);
      return;
    }

    const happened = t('error.global');
    const specific = t(`error.${error.code}` as TranslationKey, { ...error });
    const hasDetail =
      Boolean(error.code) &&
      !GENERIC_CODES.includes(String(error.code)) &&
      !isMissingCopy(specific);

    const advice = t('error.tryAgainOrContact');
    const subtitle = hasDetail ? specific : isMissingCopy(advice) ? undefined : advice;

    toast.custom(
      (active) => (
        <DialogSlide $isVisible={active.visible} $isMeasured={Boolean(active.height)}>
          <Dialog
            tone="danger"
            title={happened}
            subtitle={subtitle}
            cta={
              <Button
                variant="primary"
                size="small"
                onClick={() => {
                  dispatch(modalErrorHide());
                  resetAfterError(preventPageReload);
                }}
              >
                {t('modalError.confirm')}
              </Button>
            }
          />
        </DialogSlide>
      ),
      { id: DIALOG_TOAST_ID, duration: Infinity, position: 'top-right' }
    );

    return () => toast.dismiss(DIALOG_TOAST_ID);
  }, [error, preventPageReload, dispatch, t]);

  return null;
};
